import {useCallback, useMemo} from 'react';
import {Button, Icon, Link, Typography} from '@ezcater/tapas';
import {
  faCircleMinus,
  faClock,
  faDiamondExclamation,
  faEnvelope,
  faFax,
  faLocationDot,
  faMessage,
  faMobile,
  faPhone,
  faQuestionCircle,
} from '@fortawesome/pro-regular-svg-icons';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import EppoFlags from '@/EppoFlags';
import {useBooleanAssignment} from '@/eppoHooks';
import useEzCaterCompanyInfo from '@/hooks/useEzCaterCompanyInfo';
import useIdentity from '@/hooks/useIdentity';
import useSelfServiceReportingModal from '@/hooks/useSelfServiceReportingModal';
import useTracking from '@/hooks/useTracking';
import useWebchatEntryPoint from '@/hooks/useWebchatEntryPoint';
import {CUSTOMER_HELP_CENTER_URL, PRIVACY_PATH, TERMS_PATH} from '@/paths';
import {gtm} from '@/utils';
import {contactUsClickPayload} from '@/utils/googleAnalyticsEvents';
import trackChatStarted from '@/utils/trackChatStarted';
import {useSupportMessageEnabled} from './SupportMessage';

type ContactUsInfoModalProps = {
  onClose?: () => void;
  onStartSupportMessage?: () => void;
  className?: string;
  experimentVariation: string;
};

const trackGTMEvent = (contactType: string) => {
  gtm.dataLayer(contactUsClickPayload({contactType}));
};

const ContactMethodItem = ({icon, children}: {icon: any; children: React.ReactNode}) => (
  <div className="flex items-center">
    <Icon className="mr-3 text-green-400" icon={icon} size="small" />
    <div className="leading-tight">{children}</div>
  </div>
);

const ContactMethodLink = ({
  id,
  label,
  href,
  trackingName,
  handleClick,
  children,
}: {
  id?: string;
  label: string;
  href: string;
  trackingName: string;
  handleClick?: (trackingName: string) => void;
  children: React.ReactNode;
}) => (
  <>
    <p id={id} className="text-sm font-bold text-peppercorn-600">
      {label}
    </p>
    <Link
      aria-labelledby={id}
      className="text-sm font-bold"
      href={href}
      onClick={() => handleClick?.(trackingName)}
    >
      {children}
    </Link>
  </>
);

const ContactMethodButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <>
    <p className="text-sm font-bold text-peppercorn-600">{label}</p>
    <Button className="pl-0 font-bold" onClick={onClick} size="small" variant="buttonLink">
      {children}
    </Button>
  </>
);

const ContactMethodExternalLink = ({
  label,
  href,
  trackingName,
  handleClick,
  children,
}: {
  label: string;
  href: string;
  trackingName: string;
  handleClick?: (trackingName: string) => void;
  children: React.ReactNode;
}) => (
  <>
    <p className="text-sm font-bold text-peppercorn-600">{label}</p>
    <Link
      className="text-sm font-bold"
      href={href}
      onClick={() => handleClick?.(trackingName)}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </Link>
  </>
);

const ContactUsInfoModal = ({
  onClose,
  onStartSupportMessage,
  className = '',
  experimentVariation,
}: ContactUsInfoModalProps): React.ReactElement => {
  const {t} = useTranslation('common');
  const {openWebchat, isMobileWebchat, webchatHref} = useWebchatEntryPoint();
  const companyInfo = useEzCaterCompanyInfo();
  const contactUsPageEnabled = useBooleanAssignment(EppoFlags.ContactUsPageEnabled);

  const {track} = useTracking();

  const trackMethodSelected = useCallback(
    (methodType: string) => {
      // GTM tracking - for basic event capture (no user attribution)
      trackGTMEvent(methodType === 'sms' ? 'text' : methodType);

      // useTracking - captures user.tracking_id for attribution and warehouse analytics
      // GTM events don't include tracking IDs and don't reach the warehouse,
      // so we need ezCater events for proper user attribution and comprehensive analytics
      track('clicked-contact-us-link', {
        page: window.location.pathname,
        misc_json: JSON.stringify({
          method: methodType,
          variant: 'modal',
          experiment_variation: experimentVariation,
        }),
      });
    },
    [track, experimentVariation],
  );

  const trackSelfServiceIssueModal = useCallback(() => {
    track('self-service-reporting-modal-opened', {
      page: window.location.pathname,
    });
  }, [track]);
  const {toggleSelfServiceProviderModal} = useSelfServiceReportingModal();
  const {isLoggedIn} = useIdentity();

  const showSupportMessageWizard = useSupportMessageEnabled();

  const toggleAndTrackSelfServiceIssueModal = useCallback(() => {
    trackSelfServiceIssueModal();
    trackMethodSelected('self-service');
    toggleSelfServiceProviderModal();
    onClose?.();
  }, [trackSelfServiceIssueModal, trackMethodSelected, toggleSelfServiceProviderModal, onClose]);

  const toggleLibertyChat = useCallback(() => {
    trackMethodSelected('chat');
    openWebchat();
    onClose?.();
  }, [trackMethodSelected, openWebchat, onClose]);

  const trackAndCloseForMobileChat = useCallback(() => {
    trackChatStarted();
    trackMethodSelected('chat');
    onClose?.();
  }, [trackMethodSelected, onClose]);

  const contactComponents = useMemo(
    () => ({
      address: (
        <ContactMethodItem icon={faLocationDot}>
          <div className="text-sm leading-tight">
            <div className="font-bold text-peppercorn-600">
              {t('components.ContactUs.info.addressLine1')}
            </div>
            <div>{t('components.ContactUs.info.addressLine2')}</div>
            <div>{t('components.ContactUs.info.addressLine3')}</div>
          </div>
        </ContactMethodItem>
      ),
      chat: (
        <ContactMethodItem icon={faMessage}>
          {isMobileWebchat ? (
            <ContactMethodExternalLink
              label={t('components.ContactUs.info.chat')}
              href={webchatHref}
              trackingName="chat"
              handleClick={trackAndCloseForMobileChat}
            >
              {t('components.ContactUs.info.chatLink')}
            </ContactMethodExternalLink>
          ) : (
            <ContactMethodButton
              label={t('components.ContactUs.info.chat')}
              onClick={() => {
                trackChatStarted();
                toggleLibertyChat();
              }}
            >
              {t('components.ContactUs.info.chatLink')}
            </ContactMethodButton>
          )}
        </ContactMethodItem>
      ),
      phone: (
        <ContactMethodItem icon={faPhone}>
          <ContactMethodLink
            id="contact-us-phone-label"
            label={t('components.ContactUs.info.call')}
            href={`tel:${companyInfo.supportPhoneNumber.replace(/[^0-9]/g, '')}`}
            trackingName="phone"
            handleClick={() => trackMethodSelected('phone')}
          >
            {companyInfo.supportPhoneNumber}
          </ContactMethodLink>
        </ContactMethodItem>
      ),
      sms: (
        <>
          <ContactMethodItem icon={faMobile}>
            <ContactMethodLink
              id="contact-us-sms-label"
              label={t('components.ContactUs.info.text')}
              href={`sms:${companyInfo.supportSmsNumber}`}
              trackingName="text"
              handleClick={() => trackMethodSelected('sms')}
            >
              {companyInfo.supportSmsDisplayNumber}
            </ContactMethodLink>
          </ContactMethodItem>
          <p className="mt-2 pl-7 text-xs leading-snug text-peppercorn-600">
            <Trans
              i18nKey="common:components.ContactUs.info.smsConsentDisclaimer"
              components={{
                PrivacyLink: <Link href={PRIVACY_PATH} target="_blank" rel="noopener noreferrer" />,
                TermsLink: <Link href={TERMS_PATH} target="_blank" rel="noopener noreferrer" />,
                SmsTermsLink: (
                  <Link
                    href="https://ezcater.com/sms-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </p>
        </>
      ),
      email: showSupportMessageWizard ? (
        <ContactMethodItem icon={faEnvelope}>
          <ContactMethodButton
            label={t('components.ContactUs.info.email')}
            onClick={() => {
              track('support-message-modal-opened', {
                page: window.location.pathname,
                misc_json: JSON.stringify({
                  is_logged_in: isLoggedIn,
                  surface: 'contact_us_modal',
                }),
              });
              onStartSupportMessage?.();
            }}
          >
            {t('components.SupportMessage.button')}
          </ContactMethodButton>
        </ContactMethodItem>
      ) : (
        <ContactMethodItem icon={faEnvelope}>
          <ContactMethodLink
            id="contact-us-email-label"
            label={t('components.ContactUs.info.email')}
            href={`mailto:${companyInfo.supportEmailAddress}`}
            trackingName="email"
            handleClick={() => trackMethodSelected('email')}
          >
            {companyInfo.supportEmailAddress}
          </ContactMethodLink>
        </ContactMethodItem>
      ),
      'help-center': (
        <ContactMethodItem icon={faQuestionCircle}>
          <ContactMethodExternalLink
            label={t('components.ContactUs.info.customerHelpCenter')}
            href={CUSTOMER_HELP_CENTER_URL}
            trackingName="help-center"
            handleClick={() => trackMethodSelected('help-center')}
          >
            {t('components.ContactUs.info.visitHelpCenter')}
          </ContactMethodExternalLink>
        </ContactMethodItem>
      ),
      unsubscribe: (
        <ContactMethodItem icon={faCircleMinus}>
          <ContactMethodExternalLink
            label={t('components.ContactUs.info.physicalMailing')}
            href={companyInfo.physicalMailUnsubscribeURL}
            trackingName="unsubscribe"
            handleClick={() => trackMethodSelected('unsubscribe')}
          >
            {t('components.ContactUs.info.unsubscribeHere')}
          </ContactMethodExternalLink>
        </ContactMethodItem>
      ),
      'self-service': isLoggedIn ? (
        <ContactMethodItem icon={faDiamondExclamation}>
          <ContactMethodButton
            label={t('components.ContactUs.info.orderProblems')}
            onClick={toggleAndTrackSelfServiceIssueModal}
          >
            {t('components.ContactUs.info.reportAnIssue')}
          </ContactMethodButton>
        </ContactMethodItem>
      ) : null,
      fax: (
        <ContactMethodItem icon={faFax}>
          <ContactMethodLink
            id="contact-us-fax-label"
            label={t('components.ContactUs.info.fax')}
            href={`fax:${companyInfo.supportFaxNumber.replace(/[^0-9]/g, '')}`}
            trackingName="fax"
            handleClick={() => trackMethodSelected('fax')}
          >
            {companyInfo.supportFaxNumber}
          </ContactMethodLink>
        </ContactMethodItem>
      ),
    }),
    [
      t,
      companyInfo,
      toggleLibertyChat,
      toggleAndTrackSelfServiceIssueModal,
      isLoggedIn,
      trackMethodSelected,
      isMobileWebchat,
      webchatHref,
      trackAndCloseForMobileChat,
      showSupportMessageWizard,
      onStartSupportMessage,
      track,
    ],
  );

  // Define contact method order based on feature flag
  const contactMethodOrder = useMemo(
    () =>
      contactUsPageEnabled
        ? ['address', 'chat', 'phone', 'sms', 'email', 'help-center', 'unsubscribe', 'self-service']
        : ['phone', 'chat', 'sms', 'email', 'self-service', 'fax'],
    [contactUsPageEnabled],
  );

  // Build final contact methods array
  const contactMethods = useMemo(() => {
    return contactMethodOrder
      .map(key => ({
        key,
        component: contactComponents[key as keyof typeof contactComponents],
      }))
      .filter(method => method.component !== null);
  }, [contactMethodOrder, contactComponents]);

  return (
    <div className={twJoin('m-6 flex flex-col tablet:m-12', className)}>
      <Typography
        render={<h1>{t('components.ContactUs.info.title')}</h1>}
        variant="heading-xl"
        className="mb-6"
      />
      <div className="flex flex-grow flex-col space-y-6">
        {contactMethods.map(method => (
          <div key={method.key}>{method.component}</div>
        ))}
      </div>
      {/* Available Hours */}
      <div className="mt-6 flex items-center">
        <Icon className="mr-3 text-peppercorn-400" icon={faClock} size="small" />
        <span className="text-sm text-peppercorn-600">
          {t('components.ContactUs.info.availableHours')}
        </span>
      </div>
    </div>
  );
};

export default ContactUsInfoModal;
